export const masterDetailViewData = {
    /*
     * An expense's payments. The purchase-side mirror of invoicePaymentMDView,
     * with one level fewer: an expense is paid as a whole, so the expense is
     * both the master and its own single detail card (`detailFromMaster` on the
     * page), and its matched bank transactions hang straight off it.
     */
    expensePaymentMDView: {
        title: 'purchases.expense.payments.page.title',
        width: '1300px',
        master: {
            type: 'infoCard',
            entity: 'expense',

            sections: [
                {
                    id: 'expenseHeader',
                    columns: [
                        {
                            width: '100px',
                            rows: [
                                {
                                    scale: 'title',
                                    label: 'purchases.expense.payments.page.number',
                                    mergeFields: ['bookedNumber']
                                },
                                {
                                    scale: 'normal',
                                    label: 'purchases.expense.payments.page.date',
                                    mergeFields: ['date'],
                                    formats: {
                                        date: 'date'
                                    }
                                }
                            ]
                        },
                        {
                            rows: [
                                {
                                    scale: 'title',
                                    label: 'purchases.expense.payments.page.amount',
                                    mergeFields: ['totalGrossAmount', 'currency'],
                                    formats: {
                                        totalGrossAmount: 'amount'
                                    }
                                },
                                {
                                    scale: 'normal',
                                    label: 'purchases.expense.payments.page.amountDue',
                                    mergeFields: ['amountDue', 'currency'],
                                    formats: {
                                        amountDue: 'amount'
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    id: 'supplier',
                    columns: [
                        {
                            rows: [
                                {
                                    scale: 'normal',
                                    label: 'purchases.expense.payments.page.supplierName',
                                    mergeFields: ['supplierName']
                                }
                            ]
                        }
                    ]
                }
            ],
            detail: {
                type: 'cardListView',
                entity: 'expense',
                card: {
                    group: [
                        {
                            scale: 'normal',
                            label: 'purchases.expense.payments.detail.paymentDate',
                            mergeFields: ['paymentDate'],
                            formats: {
                                paymentDate: 'date'
                            },
                            width: '75px',
                            shrink: 1,
                        },
                        {
                            scale: 'normal',
                            label: 'purchases.expense.payments.detail.amount',
                            mergeFields: ['totalGrossAmount', 'currency'],
                            formats: {
                                totalGrossAmount: 'amount'
                            },
                            width: '75px',
                            shrink: 1,
                        },
                        {
                            scale: 'normal',
                            label: 'purchases.expense.payments.detail.amountDue',
                            mergeFields: ['amountDue', 'currency'],
                            formats: {
                                amountDue: 'amount'
                            },
                            width: '75px',
                            shrink: 1,
                        },
                        {
                            scale: 'normal',
                            label: 'purchases.expense.payments.detail.paid',
                            mergeFields: ['paid'],
                            formats: {
                                paid: {
                                    type: 'boolean',
                                    true: 'purchases.expense.payments.detail.paid.true',
                                    false: 'purchases.expense.payments.detail.paid.false'
                                }
                            },
                            width: '75px',
                            shrink: 1,
                        }
                    ]
                },
                lines: {
                    type: 'detailListView',
                    array: 'balanceModifiers',
                    variants: {
                        paymentTransaction: {
                            icon: 'transaction',
                            group: [
                                {
                                    scale: 'normal',
                                    label: 'purchases.expense.payments.line.date',
                                    mergeFields: ['date'],
                                    formats: {
                                        date: 'date'
                                    },
                                    width: '75px',
                                    shrink: 1,
                                },
                                {
                                    scale: 'normal',
                                    label: 'purchases.expense.payments.line.account',
                                    mergeFields: ['accountName'],
                                    width: '75px',
                                    shrink: 1,
                                },
                                {
                                    scale: 'normal',
                                    label: 'purchases.expense.payments.line.amount',
                                    mergeFields: ['amountPaid', 'currency'],
                                    formats: {
                                        amountPaid: 'amount'
                                    },
                                    width: '75px',
                                    shrink: 1,
                                },
                            ],
                            actions: [
                                {
                                    type: 'button',
                                    label: 'purchases.expense.payments.line.unmatch',
                                    icon: 'unlock',
                                    action: 'unMatch',
                                    path: 'expense/:id/unmatch',
                                    payload: {
                                        originId: 'id',
                                        destinationId: 'line.parentId',
                                        destinationSubId: 'line.docId'
                                    },
                                    refetchQueries: [
                                        ['expense']
                                    ]
                                }
                            ]
                        }
                    }
                },
                actions: [
                    {
                        condition: { field: 'paid', operator: 'equals', value: false },
                        line: {
                            group: [],
                            actions: [
                                {
                                    type: 'button',
                                    label: 'purchases.expense.payments.line.match',
                                    icon: 'plus',
                                    action: 'match',
                                    button: {
                                        action: 'registerPayment',
                                        actionType: 'showModal',
                                        label: 'purchases.expense.display.button.registerPayment',
                                        path: 'expense/:id/registerPayment',
                                        variant: 'yellow',
                                        modalFormRef: 'matchExpensePayment',
                                        $brightBackground: false
                                    }
                                }
                            ]
                        }
                    }
                ]
            }
        }
    },
    invoicePaymentMDView: {
        title: 'invoice.payments.page.title',
        width: '1300px',
        master: {
            type: 'infoCard',
            entity: 'invoice',

            sections: [
                {
                    id: 'invoiceHeader',
                    columns: [
                        {
                            width: '100px',
                            rows: [
                                {
                                    scale: 'title',
                                    label: 'invoice.payments.page.invoiceNumber',
                                    mergeFields: ['number']
                                },
                                {
                                    scale: 'normal',
                                    label: 'invoice.payments.page.date',
                                    mergeFields: ['date'],
                                    formats: {
                                        date: 'date'
                                    }
                                }
                            ]
                        },
                        {
                            rows: [
                                {
                                    scale: 'title',
                                    label: 'invoice.payments.page.amount',
                                    mergeFields: ['totalGrossSalesAmount', 'currency'],
                                    formats: {
                                        totalGrossSalesAmount: 'amount'
                                    }
                                },
                                {
                                    scale: 'normal',
                                    label: 'invoice.payments.page.amountDue',
                                    mergeFields: ['amountDue', 'currency'],
                                    formats: {
                                        amountDue: 'amount'
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    id: 'customer',
                    columns: [
                        {
                            rows: [
                                {
                                    scale: 'normal',
                                    label: 'invoice.payments.page.customerName',
                                    mergeFields: ['customerName']
                                }
                            ]
                        }
                    ]
                }
            ],
            detail: {
                type: 'cardListView',
                entity: 'invoicePayment',
                //title: 'invoice.payments.page.detail.title',
                card: {
                    group: [
                        {
                            scale: 'normal',
                            label: 'invoice.page.detail.date',
                            mergeFields: ['paymentDate'],
                            formats: {
                                paymentDate: 'date'
                            },
                            width: '75px',
                            shrink: 1,
                        },
                        {
                            scale: 'normal',
                            label: 'invoice.page.detail.amount',
                            mergeFields: ['paymentAmount', 'currency'],
                            formats: {
                                paymentAmount: 'amount'
                            },
                            width: '75px',
                            shrink: 1,
                        },
                        {
                            scale: 'normal',
                            label: 'invoice.page.detail.amountDue',
                            mergeFields: ['amountDueOriginCurrency', 'currency'],
                            formats: {
                                amountDueOriginCurrency: 'amount'
                            },
                            width: '75px',
                            shrink: 1,
                        },
                        {
                            scale: 'normal',
                            label: 'invoice.page.detail.paid',
                            mergeFields: ['paid'],
                            formats: {
                                paid: {
                                    type: 'boolean',
                                    true: 'invoice.page.detail.paid.true',
                                    false: 'invoice.page.detail.paid.false'
                                }
                            },
                            width: '75px',
                            shrink: 1,

                        }
                    ]
                },
                lines: {
                    type: 'detailListView',
                    array: 'balanceModifiers',
                    variants: {
                        paymentTransaction: {
                            icon: 'transaction',
                            group: [
                                {
                                    scale: 'normal',
                                    label: 'invoice.page.detail.line.date',
                                    mergeFields: ['date'],
                                    formats: {
                                        date: 'date'
                                    },
                                    width: '75px',
                                    shrink: 1,
                                },
                                {
                                    scale: 'normal',
                                    label: 'invoice.page.detail.line.account',
                                    mergeFields: ['accountName'],
                                    width: '75px',
                                    shrink: 1,
                                },
                                {
                                    scale: 'normal',
                                    label: 'invoice.page.detail.line.amount',
                                    mergeFields: ['amountPaid', 'currency'],
                                    formats: {
                                        amountPaid: 'amount'
                                    },
                                    width: '75px',
                                    shrink: 1,
                                },
                            ],
                            actions: [
                                {
                                    type: 'button',
                                    label: 'invoice.page.detail.line.unmatch',
                                    icon: 'unlock',
                                    action: 'unMatch',
                                    path: 'invoicePayment/:id/unmatch',
                                    payload: {
                                        originId: 'id',
                                        destinationId: 'line.parentId',
                                        destinationSubId: 'line.docId'
                                    },
                                    // Additional queries to refetch after action completes
                                    // This ensures the invoice list page and other related pages are refreshed
                                    refetchQueries: [
                                        ['invoice'],         // Refetch all invoice queries (list and detail)
                                        ['invoicePayment']   // Refetch all invoicePayment queries
                                    ]
                                }
                            ]
                        },
                        creditNote: {
                            icon: 'creditNote',
                            group: [
                                {
                                    scale: 'normal',
                                    label: 'invoice.page.detail.line.date',
                                    mergeFields: ['date'],
                                    formats: {
                                        date: 'date'
                                    },
                                    width: '75px',
                                    shrink: 1,
                                },
                                {
                                    scale: 'normal',
                                    label: 'invoice.page.detail.line.type',
                                    mergeFields: ['sales.document.type.creditNote'],
                                    formats: {
                                        'sales.document.type.creditNote': 'label'
                                    },
                                    width: '75px',
                                    shrink: 1,
                                },
                                {
                                    scale: 'normal',
                                    label: 'invoice.page.detail.line.amount',
                                    mergeFields: ['amount', 'currency'],
                                    formats: {
                                        amount: 'amount'
                                    },
                                    width: '75px',
                                    shrink: 1,
                                },
                            ],
                            actions: [

                            ]
                        }
                    }
                },
                actions: [
                    {
                        condition: { field: 'paid', operator: 'equals', value: false },
                        line: {
                            group: [
                                {
                                    scale: 'normal',
                                    label: 'invoice.page.action.message',
                                    mergeFields: [],
                                    text: 'invoice.page.action.message.content',
                                    width: '275px',
                                    shrink: 1,
                                },
                                {
                                    scale: 'normal',
                                    label: 'invoice.page.detail.amountDue',
                                    mergeFields: ['amountDueOriginCurrency', 'currency'],
                                    formats: {
                                        amountDueOriginCurrency: 'amount'
                                    },
                                    width: '75px',
                                    shrink: 1,
                                },
                            ],
                            actions: [
                                {
                                    type: 'button',
                                    label: 'invoice.page.detail.line.match',
                                    icon: 'plus',
                                    action: 'match',
                                    button: {
                                        action: 'registerPayment',
                                        actionType: 'showModal',
                                        label: 'invoice.display.button.registerPayment',
                                        path: 'invoice/:id/registerPayment',
                                        variant: 'yellow',
                                        modalFormRef: 'matchPayment',
                                        $brightBackground: false
                                    }
                                }
                            ]
                        }
                    }
                ]
            }

        }
    }
}
